import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  FiShield, 
  FiLock, 
  FiEye, 
  FiAlertTriangle, 
  FiCheckCircle,
  FiArrowRight,
  FiMonitor,
  FiKey
} from 'react-icons/fi';
import { trackPageView } from '../utils/analytics';
import './Security.css';

const Security = () => {
  useEffect(() => {
    // Track page visit with comprehensive analytics
    trackPageView('/security', {
      page_title: 'LogCrest Security - Advanced Threat Detection',
      page_category: 'security'
    });
  }, []);

  const features = [
    {
      icon: <FiShield />,
      title: 'Advanced Threat Detection',
      description: 'AI-powered security monitoring that detects threats in real-time across your entire infrastructure.'
    },
    {
      icon: <FiLock />,
      title: 'Zero Trust Architecture',
      description: 'Implement zero trust security principles with comprehensive access controls and encryption.'
    },
    {
      icon: <FiEye />,
      title: 'Compliance Monitoring',
      description: 'Automated compliance checks for SOC2, GDPR, HIPAA, and other regulatory requirements.'
    },
    {
      icon: <FiAlertTriangle />,
      title: 'Security Incident Response',
      description: 'Automated incident response workflows with intelligent threat containment and remediation.'
    }
  ];

  const capabilities = [
    'Real-time threat detection and analysis',
    'Behavioral anomaly detection',
    'Automated vulnerability scanning',
    'Compliance reporting and auditing',
    'Identity and access management',
    'Data encryption at rest and in transit',
    'Security event correlation',
    'Incident response automation'
  ];

  const benefits = [
    {
      metric: '99.9%',
      label: 'Threat Detection Accuracy',
      description: 'AI-powered security monitoring'
    },
    {
      metric: '50%',
      label: 'Faster Incident Response',
      description: 'Automated threat containment'
    },
    {
      metric: '100%',
      label: 'Compliance Coverage',
      description: 'Automated regulatory compliance'
    },
    {
      metric: '24/7',
      label: 'Security Monitoring',
      description: 'Continuous threat protection'
    }
  ];

  return (
    <div className="security">
      {/* Hero Section */}
      <section className="security-hero">
        <div className="container">
          <div className="security-hero-content">
            <motion.div
              className="hero-text"
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1>Enterprise-Grade Security</h1>
              <p>
                Protect your infrastructure with advanced security monitoring, threat detection, 
                and compliance management. LogCrest provides comprehensive security solutions 
                that keep your data and systems safe.
              </p>
              <div className="hero-buttons">
                <Link to="/pricing" className="btn btn-secondary">
                  Get Started
                  <FiArrowRight />
                </Link>
              </div>
            </motion.div>
            <motion.div
              className="hero-visual"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="security-dashboard">
                <div className="dashboard-header">
                  <div className="dashboard-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <span className="dashboard-title">LogCrest Security Center</span>
                </div>
                <div className="dashboard-content">
                  <div className="security-metrics">
                    <div className="metric-item secure">
                      <FiShield />
                      <span>All Systems Secure</span>
                    </div>
                    <div className="metric-item warning">
                      <FiAlertTriangle />
                      <span>2 Threats Blocked</span>
                    </div>
                    <div className="metric-item info">
                      <FiEye />
                      <span>Compliance: 100%</span>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="security-features">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2>Comprehensive security protection</h2>
            <div className="grid grid-2">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  className="feature-card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="feature-icon">
                    {feature.icon}
                  </div>
                  <h3>{feature.title}</h3>
                  <p>{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Capabilities Section */}
      <section className="security-capabilities">
        <div className="container">
          <div className="capabilities-content">
            <motion.div
              className="capabilities-text"
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2>Advanced security capabilities</h2>
              <p>
                Our security platform provides enterprise-grade protection with AI-powered 
                threat detection, automated compliance monitoring, and comprehensive incident 
                response capabilities.
              </p>
              <ul className="capabilities-list">
                {capabilities.map((capability, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <FiCheckCircle />
                    {capability}
                  </motion.li>
                ))}
              </ul>
              <Link to="/demo" className="btn btn-primary">
                See Security in Action
                <FiArrowRight />
              </Link>
            </motion.div>
            <motion.div
              className="capabilities-visual"
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="security-workflow">
                <div className="workflow-step">
                  <div className="step-icon">
                    <FiMonitor />
                  </div>
                  <div className="step-content">
                    <h4>Monitor</h4>
                    <p>Continuous security monitoring</p>
                  </div>
                </div>
                <div className="workflow-arrow">→</div>
                <div className="workflow-step">
                  <div className="step-icon">
                    <FiKey />
                  </div>
                  <div className="step-content">
                    <h4>Detect</h4>
                    <p>AI threat identification</p>
                  </div>
                </div>
                <div className="workflow-arrow">→</div>
                <div className="workflow-step">
                  <div className="step-icon">
                    <FiShield />
                  </div>
                  <div className="step-content">
                    <h4>Protect</h4>
                    <p>Automated response</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="security-benefits">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2>Security you can trust</h2>
            <div className="benefits-grid">
              {benefits.map((benefit, index) => (
                <motion.div
                  key={index}
                  className="benefit-card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <div className="benefit-metric">{benefit.metric}</div>
                  <div className="benefit-label">{benefit.label}</div>
                  <div className="benefit-description">{benefit.description}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="security-cta">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2>Secure your infrastructure today</h2>
            <p>
              Join leading organizations who trust LogCrest Security to protect their 
              critical infrastructure. Get enterprise-grade security with intelligent automation.
            </p>
            <div className="cta-buttons">
              <Link to="/pricing" className="btn btn-primary">
                Contact Sales
                <FiArrowRight />
              </Link>
              <Link to="/demo" className="btn btn-secondary">
                Schedule Demo
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Security;
